import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {CodingAssistantButton} from '@github-ui/coding-assistant-button'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {noop} from '@github-ui/noop'
import {CopyIcon, KebabHorizontalIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {OptionConfig} from '../OptionConfig'
import type {HeaderMenu$key} from './__generated__/HeaderMenu.graphql'
import {CreateIssueButtonShell} from './CreateIssueButtonShell'
import styles from './HeaderMenu.module.css'

const CreateIssueButton = React.lazy(async () => {
  const m = await import('@github-ui/issue-create/CreateIssueButton')
  return {default: m.CreateIssueButton}
})

export type HeaderMenuBaseProps = {
  optionConfig: OptionConfig
  setIsIssueTitleEditActive?: (isActive: boolean) => void
  issueTitle?: string
}

type HeaderMenuProps = {
  headerMenuData: HeaderMenu$key
} & HeaderMenuBaseProps

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const HeaderMenuFragment = graphql`
  fragment HeaderMenu on Issue {
    number
    viewerCanUpdateNext
    repository {
      name
      owner {
        login
      }
      isArchived
      isWritable
    }
    url
  }
`

export function HeaderMenu({
  headerMenuData,
  setIsIssueTitleEditActive,
  optionConfig: {
    customEditMenuEntries,
    navigate,
    showIssueCreateButton,
    additionalHeaderActions,
    onClose,
    useViewportQueries,
    isReadonly,
  },
}: HeaderMenuProps) {
  const {number: issueNumber, viewerCanUpdateNext, url, repository} = useFragment(HeaderMenuFragment, headerMenuData)

  const anchorRef = useRef<HTMLButtonElement>(null)
  const codingAssistantRef = useRef<HTMLElement | null>(null)
  const canEditTitle = viewerCanUpdateNext && !isReadonly
  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  // When the sticky header is enabled, the open-in-agent, copy-link, and create-issue actions render in the sticky top container instead.
  const showInlineActions = !stickyHeaderEnabled
  const showCreateIssueButton = showIssueCreateButton && repository.isWritable && !stickyHeaderEnabled

  const agentAssignmentEnabled = isFeatureEnabled('issue_cca_modal_open')
  const [modalOpen, setModalOpen] = useState(false)

  const handleOpenModal = useCallback(() => {
    setModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  const editIssueTitleButton = useMemo(
    () =>
      canEditTitle ? (
        <Button
          data-testid={TEST_IDS.editIssueTitleButton}
          aria-label={LABELS.editTitle}
          onClick={() => setIsIssueTitleEditActive && setIsIssueTitleEditActive(true)}
          size="medium"
        >
          {BUTTON_LABELS.editTitle}
        </Button>
      ) : null,
    [setIsIssueTitleEditActive, canEditTitle],
  )

  return (
    <div className={clsx(styles.menuActionsContainer, stickyHeaderEnabled && styles.menuActionsContainer_stickyHeader)}>
      {isFeatureEnabled('issues_open_in_tool_links') && !isFeatureEnabled('issues_sticky_header') && (
        <CodingAssistantButton
          anchorRef={codingAssistantRef}
          className="mr-1"
          url={url}
          buttonLabel="Open in coding assistant"
          promptBuilder={(issueUrl: string) => `Work on the following GitHub issue: ${issueUrl}.`}
          size="medium"
          showSettingsLink={isFeatureEnabled('coding_tool_settings')}
          iconOnly
          onAssignToAgent={agentAssignmentEnabled ? handleOpenModal : undefined}
        />
      )}
      {agentAssignmentEnabled && modalOpen && (
        <CopilotAssignmentModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          repository={repository}
          issueNumbers={[issueNumber]}
          returnFocusRef={codingAssistantRef}
        />
      )}
      {editIssueTitleButton}
      <div
        className={clsx(
          styles.buttonContainer,
          useViewportQueries ? styles.buttonContainer_viewport : styles.buttonContainer_container,
        )}
      >
        {showCreateIssueButton && (
          <Suspense fallback={<CreateIssueButtonShell />}>
            <CreateIssueButton
              label={BUTTON_LABELS.new}
              navigate={navigate}
              optionConfig={{
                showRepositoryPicker: false,
                issueCreateArguments: {
                  repository: {
                    owner: repository.owner.login,
                    name: repository.name,
                  },
                },
                showFullScreenButton: true,
                // Only navigate if we're in the Repo#Index (ie, have a scoped repository)
                navigateToFullScreenOnTemplateChoice: navigate !== noop && repository !== null,
                canBypassTemplateSelection: true,
                navigate,
              }}
            />
          </Suspense>
        )}
      </div>
      {showInlineActions && (
        <CopyToClipboardButton
          textToCopy={url}
          ariaLabel={BUTTON_LABELS.copyIssueLink}
          icon={CopyIcon}
          tooltipProps={{direction: 's'}}
        />
      )}
      {additionalHeaderActions}
      {customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              ref={anchorRef}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}

export function StickyHeaderMenu({
  headerMenuData,
  optionConfig: {customEditMenuEntries, additionalHeaderActions, onClose},
}: HeaderMenuProps) {
  const {url} = useFragment(HeaderMenuFragment, headerMenuData)

  const anchorRef = useRef<HTMLButtonElement>(null)

  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')

  return (
    <div className={clsx(styles.menuActionsContainer, stickyHeaderEnabled && styles.menuActionsContainer_stickyHeader)}>
      <CopyToClipboardButton
        textToCopy={url}
        ariaLabel={BUTTON_LABELS.copyIssueLink}
        icon={CopyIcon}
        tooltipProps={{direction: 's'}}
      />
      {additionalHeaderActions}
      {customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              tooltipDirection="n"
              ref={anchorRef}
              className={styles.kebabMenuButton}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}
